import { Bar, CartesianGrid, ComposedChart, Line, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { SX_PALETTE } from '@siliconx/shared/ui/charts';
import { formatMwh, formatTonnes } from '../format';
import { mergeMonthlyEmissionsWithMwh, type MonthlyEmissionsInput, type MonthlyMwhInput } from './monthlyEmissionsMwh';

const BORDER = '#e4e4e7';
const MUTED = '#71717a';

/**
 * SIL-476 — monthly emissions (Scope 2 + Scope 3, stacked bars, left axis)
 * with the new MWh overlay (readiness doc gap #4 — right axis, amber line
 * per the mock). REAL data on both axes: the emissions bars are the SAME
 * `useMonthlySeries` points the Overview tab's own monthly chart plots
 * (never re-derived here); the MWh line is this route's new
 * `telemetry_daily_agg` aggregation. The two source surfaces have
 * independent history depths — `mergeMonthlyEmissionsWithMwh` nulls
 * whichever half a given month doesn't have data for, so neither series is
 * ever padded with a fabricated value to fill a gap.
 */
export function MonthlyEmissionsMwhChart({
  emissions,
  mwh,
}: {
  emissions: MonthlyEmissionsInput[];
  mwh: MonthlyMwhInput[];
}) {
  const points = mergeMonthlyEmissionsWithMwh(emissions, mwh);

  if (points.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-xs text-zinc-400">
        No monthly history computed yet.
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={220}>
      <ComposedChart data={points} margin={{ top: 4, right: 8, bottom: 0, left: 8 }}>
        <CartesianGrid strokeDasharray="3 3" stroke={BORDER} vertical={false} />
        <XAxis dataKey="periodKey" stroke={MUTED} fontSize={12} tickLine={false} />
        <YAxis
          yAxisId="mass"
          stroke={MUTED}
          fontSize={12}
          tickLine={false}
          width={56}
          tickFormatter={(v: number) => formatTonnes(v)}
        />
        <YAxis
          yAxisId="energy"
          orientation="right"
          stroke={SX_PALETTE[4]}
          fontSize={12}
          tickLine={false}
          width={64}
          tickFormatter={(v: number) => formatMwh(v)}
        />
        <Tooltip
          contentStyle={{ fontSize: 12, borderRadius: 6, borderColor: BORDER }}
          formatter={(v: number, name: string) => (name === 'MWh' ? formatMwh(v) : formatTonnes(v))}
        />
        <Bar yAxisId="mass" dataKey="scope2Kgco2e" name="Scope 2" stackId="s" fill={SX_PALETTE[1]} />
        <Bar yAxisId="mass" dataKey="scope3Kgco2e" name="Scope 3" stackId="s" fill={SX_PALETTE[3]} />
        <Line
          yAxisId="energy"
          type="monotone"
          dataKey="mwh"
          name="MWh"
          stroke={SX_PALETTE[4]}
          strokeWidth={2}
          dot={false}
          connectNulls
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
}
